import type { RootState } from '../app/store';
import { Note, Thesaurus, Perimetre, Entree } from './api';
import { StatusBarData, EntreeFormData } from './ui';

// États de chargement constants
export const LOADING_STATES = {
    IDLE: 'idle',
    LOADING: 'loading',
    SUCCEEDED: 'succeeded',
    FAILED: 'failed'
} as const;

export type LoadingState = typeof LOADING_STATES[keyof typeof LOADING_STATES];

// Configuration de tri
export interface SortButton {
    displayName: string;
    value: 'asc' | 'desc' | null;
    index: number;
}

export type SortConfiguration = Record<string, SortButton>;

// État Redux pour les modales et UI
export interface ModalEntreeState {
    open: boolean;
    from: string | null;
    entreeInit: EntreeFormData | null;
}

export interface ModalPerimState {
    open: boolean;
    perimId: string | null;
}

export interface ModalAlertState {
    open: boolean;
    title: string;
    content: string;
}

export interface ModalsState {
    modalEntreeVisible: ModalEntreeState;
    modalNoteVisible: boolean;
    modalPerimVisible: ModalPerimState;
    showFilePanel: boolean;
    modalAlert: ModalAlertState;
    statusBar: StatusBarData;
}

// État Redux pour les notes
export interface NotesState {
    data: Note[];
    activeItem: Note | Record<string, never>;
    status: LoadingState;
    error: string | null;
    bSort: SortConfiguration;
}

// État Redux pour le thésaurus
export interface ThesaurusState {
    data: Thesaurus[];
    activeItem: Thesaurus | Record<string, never>;
    status: LoadingState;
    error: string | null;
    bSort: SortConfiguration;
}

// État Redux pour les périmètres
export interface PerimetresState {
    data: Perimetre[];
    activeItem: Perimetre | Record<string, never>;
    status: LoadingState;
    error: string | null;
    bSort: SortConfiguration;
}

// État Redux pour les entrées
export interface EntreesState {
    data: Entree[];
    activeItem: Entree | Record<string, never>;
    status: LoadingState;
    error: string | null;
    bSort: SortConfiguration;
}

// État Redux pour les modales
export interface ModalsState {
    isNoteModalOpen: boolean;
    isThesaurusModalOpen: boolean;
    isPerimetreModalOpen: boolean;
    isEntreeModalOpen: boolean;
    modalMode: 'create' | 'edit' | 'view';
}

// État Redux pour les paramètres
export interface SettingsState {
    isMobile: boolean;
    theme: 'light' | 'dark';
    language: 'fr' | 'en';
    sidebarCollapsed: boolean;
}

// ⚠️ Ne pas définir RootState ici. Utiliser :
// import { RootState } from '../app/store';

// Types pour les actions Redux
export interface AsyncAction {
    type: string;
    payload?: any;
    meta?: {
        requestId: string;
        requestStatus: 'pending' | 'fulfilled' | 'rejected';
    };
}

// Types pour les thunks
export interface ThunkAPI {
    dispatch: any;
    getState: () => RootState;
    rejectWithValue: (value: any) => any;
}
